import axios from 'axios';

// Taux de change de Guydeez (CTO, 2026-10-05) : la table reglee dans l'admin
// (/exchange-rates), servie par GET /api/exchange-rates, base EUR. Plus aucun appel a une
// API tierce depuis le navigateur — le site affiche exactement les taux avec lesquels le
// backend encaisse et facture.
//
// Une seule table pour toutes les paires : 1 EUR = rates[X] ; une paire sans EUR passe par
// lui (USD -> GBP = GBP / USD). Mise en cache 60 s — la meme duree que le backend, pour
// qu'apres un changement de taux le prix affiche et le prix controle au paiement ne
// divergent pas plus d'une minute (au-dela de 2 %, le paiement serait refuse) — et
// requetes simultanees regroupees : une page ville rend des dizaines de cartes.
type RatesTable = Record<string, number>;
let ratesCache: { rates: RatesTable; expiresAt: number } | null = null;
let inflight: Promise<RatesTable | null> | null = null;
const TTL_MS = 60 * 1000;

const loadEurTable = async (): Promise<RatesTable | null> => {
  const now = Date.now();
  if (ratesCache && ratesCache.expiresAt > now) return ratesCache.rates;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/exchange-rates`);
      const rates: RatesTable = { EUR: 1, ...(response.data?.rates || {}) };
      ratesCache = { rates, expiresAt: Date.now() + TTL_MS };
      return rates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

/** Table complete pour une devise de base : rates[X] = combien vaut 1 `base` en X. */
const loadRates = async (baseCurrency: string): Promise<RatesTable | null> => {
  const eur = await loadEurTable();
  if (!eur) return null;
  const base = eur[baseCurrency.toUpperCase()];
  if (!(base > 0)) return null;
  const out: RatesTable = {};
  Object.entries(eur).forEach(([code, v]) => { out[code] = v / base; });
  return out;
};

export const fetchExchangeRate = async (baseCurrency: string, targetCurrency: string) => {
  if (!baseCurrency || !targetCurrency) return null;
  if (baseCurrency.toUpperCase() === targetCurrency.toUpperCase()) return 1;
  const rates = await loadRates(baseCurrency);
  if (!rates) return null;
  const rate = rates[targetCurrency.toUpperCase()] ?? rates[targetCurrency];
  return typeof rate === 'number' ? rate : null;
};
